// P2b-3 §2: gate predicates for the Meta and Google execution grids.
//
// A row appears on the Meta grid iff EVERY one of these holds:
//   - metaReadyToBuild === true
//   - effectiveRunsMeta(row, account) === true
//   - hasEffectiveBudget(metaMonthlyBudget, metaCampaignIncrease)
//   - effectiveMetaAdType(row, campaign, locationProfile) !== null
//   - rowType !== 'opt_out' AND rowType !== 'unassigned'
//   - deletedAt === null
//
// ⚠️ THE BUDGET CONDITION WAS `metaMonthlyBudget > 0` UNTIL 2026-08-06, which
// disagreed with the ready gate's `monthly > 0 || campaignIncrease > 0`. A row
// funded entirely by a campaign increase passed the ready gate and showed a
// valid daily budget, then never appeared here — silently unbuildable. Both
// sides now import the one shared hasEffectiveBudget; do not re-inline it.
//
// Google has no adType concept — Google campaigns are conceptually traffic.
//
// The rollout-level archived check is NOT folded in here: it's a once-per-
// request decision that callers handle before iterating rows. Keeping the
// per-row helper free of rollout state means it works equally well for
// server-side pre-filtering AND for client-side count derivation from
// cached master-grid rows.
//
// Pure: no I/O, structural input types so both the client `AccountCampaign`
// (ISO dates) and the server `StoredAccountCampaign` (Firestore Timestamps)
// satisfy the constraints. None of the predicates inspect the date fields.

import type { AccountCampaignRowType } from '../types/accountCampaign.js';
import type { MetaAdCopy } from '../types/campaign.js';
import type { Account } from '../types/account.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';
import {
  effectiveRunsGoogle,
  effectiveRunsMeta,
  type CampaignChannels,
} from './platformEnabled.js';
import { effectiveMetaAdType } from './effectiveMetaAdType.js';
import { hasEffectiveBudget } from './effectiveBudget.js';

// `deletedAt` is `IsoTimestamp | null` on the wire shape and
// `Timestamp | null` server-side. Both compare equal to `null` when the row
// is live, so the helper accepts `unknown` and uses `== null` (matches both
// `null` and the legacy-undefined case).
// The *CampaignIncrease fields are REQUIRED (not optional-with-a-default) so
// TypeScript forces every construction site to supply them. Defaulting a
// missing increase to 0 would silently reproduce the exact bug this fixes:
// an increase-only row falling off the grid.
export type MetaGridFilterableRow = {
  metaReadyToBuild: boolean;
  metaMonthlyBudget: number | null;
  metaCampaignIncrease: number;
  rowType: AccountCampaignRowType;
  metaEnabled: boolean | null;
  metaAdType?: 'traffic' | 'lead_gen' | null;
  deletedAt: unknown;
};

export type GoogleGridFilterableRow = {
  googleReadyToBuild: boolean;
  googleMonthlyBudget: number | null;
  googleCampaignIncrease: number;
  rowType: AccountCampaignRowType;
  googleEnabled: boolean | null;
  deletedAt: unknown;
};

export type FilterableAccount = Pick<Account, 'runsMeta' | 'runsGoogle'>;

export type FilterableCampaign = {
  metaAdCopy: Pick<MetaAdCopy, 'metaAdType'>;
  channels?: CampaignChannels;
};

// The Google gate needs only the campaign's channels. `null` = no campaign
// loaded / assigned, read as 'both' (the unchanged pre-channels behaviour).
// REQUIRED (not optional) so every caller has to decide what to pass.
export type ChannelsCampaign = { channels?: CampaignChannels } | null;

export type FilterableLocationProfile = Pick<
  FranchiseLocationProfile,
  'defaultMetaAdType'
>;

export function passesMetaGridFilter(
  row: MetaGridFilterableRow,
  account: FilterableAccount,
  campaign: FilterableCampaign | null,
  locationProfile: FilterableLocationProfile | null,
): boolean {
  if (row.deletedAt != null) return false;
  if (row.rowType === 'opt_out' || row.rowType === 'unassigned') return false;
  if (!row.metaReadyToBuild) return false;
  if (!effectiveRunsMeta(account.runsMeta, row.metaEnabled, campaign?.channels)) return false;
  if (!hasEffectiveBudget(row.metaMonthlyBudget, row.metaCampaignIncrease)) return false;
  // Without a Campaign there's no metaAdType to resolve. A campaign-typed
  // row whose Campaign doc has been deleted (or isn't yet loaded on the
  // client) can't be assessed, so we keep it off the grid.
  if (campaign === null) return false;
  if (
    effectiveMetaAdType({ metaAdType: row.metaAdType ?? null }, campaign, locationProfile) ===
    null
  )
    return false;
  return true;
}

export function passesGoogleGridFilter(
  row: GoogleGridFilterableRow,
  account: FilterableAccount,
  campaign: ChannelsCampaign,
): boolean {
  if (row.deletedAt != null) return false;
  if (row.rowType === 'opt_out' || row.rowType === 'unassigned') return false;
  if (!row.googleReadyToBuild) return false;
  if (!effectiveRunsGoogle(account.runsGoogle, row.googleEnabled, campaign?.channels)) {
    return false;
  }
  if (!hasEffectiveBudget(row.googleMonthlyBudget, row.googleCampaignIncrease)) return false;
  return true;
}

// Convenience: feed both helpers from the same set of inputs and return
// the appropriate one. Lets callers dispatch on a `'meta' | 'google'`
// parameter without re-implementing the branch.
//
// `campaign` and `locationProfile` are ignored on the Google branch.
export function passesGridFilter(
  platform: 'meta' | 'google',
  row: MetaGridFilterableRow & GoogleGridFilterableRow,
  account: FilterableAccount,
  campaign: FilterableCampaign | null,
  locationProfile: FilterableLocationProfile | null,
): boolean {
  return platform === 'meta'
    ? passesMetaGridFilter(row, account, campaign, locationProfile)
    : passesGoogleGridFilter(row, account, campaign);
}

// Discriminated reasons row falls off the grid. Useful for telemetry /
// debug logging — not surfaced to users (the empty-state copy in §6.3 is
// generic). Returns null when the row passes.
export type MetaGridRejection =
  | 'deleted'
  | 'opt_out'
  | 'unassigned'
  | 'not_ready'
  | 'platform_off'
  | 'no_budget'
  | 'no_campaign'
  | 'no_ad_type';

export function explainMetaGridRejection(
  row: MetaGridFilterableRow,
  account: FilterableAccount,
  campaign: FilterableCampaign | null,
  locationProfile: FilterableLocationProfile | null,
): MetaGridRejection | null {
  if (row.deletedAt != null) return 'deleted';
  if (row.rowType === 'opt_out') return 'opt_out';
  if (row.rowType === 'unassigned') return 'unassigned';
  if (!row.metaReadyToBuild) return 'not_ready';
  if (!effectiveRunsMeta(account.runsMeta, row.metaEnabled, campaign?.channels)) {
    return 'platform_off';
  }
  if (!hasEffectiveBudget(row.metaMonthlyBudget, row.metaCampaignIncrease)) return 'no_budget';
  if (campaign === null) return 'no_campaign';
  if (
    effectiveMetaAdType({ metaAdType: row.metaAdType ?? null }, campaign, locationProfile) ===
    null
  )
    return 'no_ad_type';
  return null;
}

export type GoogleGridRejection =
  | 'deleted'
  | 'opt_out'
  | 'unassigned'
  | 'not_ready'
  | 'platform_off'
  | 'no_budget';

export function explainGoogleGridRejection(
  row: GoogleGridFilterableRow,
  account: FilterableAccount,
  campaign: ChannelsCampaign,
): GoogleGridRejection | null {
  if (row.deletedAt != null) return 'deleted';
  if (row.rowType === 'opt_out') return 'opt_out';
  if (row.rowType === 'unassigned') return 'unassigned';
  if (!row.googleReadyToBuild) return 'not_ready';
  if (!effectiveRunsGoogle(account.runsGoogle, row.googleEnabled, campaign?.channels)) {
    return 'platform_off';
  }
  if (!hasEffectiveBudget(row.googleMonthlyBudget, row.googleCampaignIncrease)) return 'no_budget';
  return null;
}

